import { User } from "../user/user.model";
import { Client } from "../client/client.model";
import { Rate } from "./rate-type";
import { RateLocation } from "./rate-location/rate-location.type";
import { Carrier } from "../carrier/carrier.model";
import { RateMapper } from "./rate.mapper";
import { BaseLinkedRate } from "./rate-base-linked.model";
import currency from "currency.js";
import { LclWeightBracketPrices } from "../shared/lcl-weight-bracket/lcl-weight-bracket-prices.model";

export class LclRate extends BaseLinkedRate {
  constructor(
    protected id: number,
    protected name: string,
    protected discriminator: string,
    protected createdBy?: User,
    protected updatedBy?: User,
    protected createdAt?: Date,
    protected updatedAt?: Date,
    protected type?: string,
    protected source?: string,
    protected status?: string,
    protected internalNote?: string,
    protected externalNote?: string,
    protected pmNote?: string,
    protected comment?: string,
    protected deepLink?: string,
    protected carrier?: Carrier,
    protected validity?: Date[],
    protected currency?: string,
    protected weightPrice?: number,
    protected measurementPrice?: number,
    protected weightTotals?: number,
    protected measurementTotals?: number,
    protected weightUnit?: string,
    protected minPrice?: number,
    protected blPrice?: number,
    protected availableOnline?: boolean,
    public maxWeightPerShipment?: number,
    public maxWeightPerPiece?: number,
    public maxMeasurementPerShipment?: number,
    public maxMeasurementPerPiece?: number,
    public maxTaxWeight?: number,
    public maxLength?: number,
    public maxHeight?: number,
    public maxWidth?: number,
    public maxLWH?: number,
    public weightBracketMargins?: LclWeightBracketPrices,
    protected originLocation?: RateLocation,
    protected destinationLocation?: RateLocation,
    protected contractNumber?: string,
    protected restrictedClients?: Client[],
    protected restrictedUsers?: User[],
    protected rootParent?: Rate,
    protected incoterms: string[] = [],
    // TODO: We should consider a new model LclFreightRate for this
    protected chargeableWeight?: number,
    protected chargeableWeightPrice?: number,
    protected chargeableWeightMargin?: number,
    protected blMargin?: number,
    public marginType?: string,
    public minDensity?: number,
    public maxDensity?: number,
  ) {
    super(
      id,
      name,
      discriminator,
      createdBy,
      updatedBy,
      createdAt,
      updatedAt,
      type,
      source,
      status,
      internalNote,
      externalNote,
      pmNote,
      comment,
      deepLink,
      carrier,
      validity,
      currency,
      originLocation,
      destinationLocation,
      contractNumber,
      restrictedClients,
      restrictedUsers,
      rootParent,
    );
  }

  public getWeightPrice(): number {
    return this.weightPrice;
  }

  public setWeightPrice(param: number) {
    this.weightPrice = param;
  }

  public getMeasurementPrice(): number {
    return this.measurementPrice;
  }

  public setMeasurementPrice(param: number) {
    this.measurementPrice = param;
  }

  public getConversionRatio() {
    const ratio = currency(this.getMeasurementPrice(), { precision: 6 }).divide(
      this.getWeightPrice(),
    ).value;

    return isFinite(ratio) ? ratio : 1;
  }

  public getWeightTotals(): number {
    return this.weightTotals;
  }

  public get weightTotalsInTon(): number {
    return this.weightTotals * 1000;
  }

  public setWeightTotals(param: number) {
    this.weightTotals = param;
  }

  public getMeasurementTotals(): number {
    return this.measurementTotals;
  }

  public setMeasurementTotals(param: number) {
    this.measurementTotals = param;
  }

  public getWeightUnit(): string {
    return this.weightUnit;
  }

  public setWeightUnit(param: string) {
    this.weightUnit = param;
  }

  public getMinPrice(): number {
    return this.minPrice;
  }

  public setMinPrice(param: number) {
    this.minPrice = param;
  }

  public getBlPrice(): number {
    return this.blPrice || 0;
  }

  public setBlPrice(param: number) {
    this.blPrice = param;
  }

  public getChargeableWeight(): number {
    return this.chargeableWeight;
  }
  public setChargeableWeight(value: number) {
    this.chargeableWeight = value;
  }

  public getChargeableWeightPrice(): number {
    return this.chargeableWeightPrice;
  }
  public setChargeableWeightPrice(value: number) {
    this.chargeableWeightPrice = value;
  }

  public getChargeableWeightMargin(): number {
    return this.chargeableWeightMargin;
  }
  public setChargeableWeightMargin(value: number) {
    this.chargeableWeightMargin = value;
  }

  public getBlMargin(): number {
    return this.blMargin;
  }
  public setBlMargin(value: number) {
    this.blMargin = value;
  }

  public getAvailableOnline() {
    return this.availableOnline;
  }

  public setAvailableOnline(availableOnline) {
    this.availableOnline = availableOnline;
  }

  public getIncoterms(): string[] {
    return this.incoterms;
  }

  public setIncoterms(incoterms: string[]): void {
    this.incoterms = incoterms;
  }

  public getClone(): Rate {
    return RateMapper.map({
      id: this.id,
      name: this.name,
      discriminator: this.discriminator,
      createdBy: this.createdBy,
      updatedBy: this.updatedBy,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      type: this.type,
      status: this.status,
      internalNote: this.internalNote,
      externalNote: this.externalNote,
      carrier: this.carrier,
      validity: this.validity,
      currency: this.currency,
      weightPrice: this.weightPrice,
      measurementPrice: this.measurementPrice,
      weightTotals: this.weightTotals,
      measurementPriceTotals: this.measurementTotals,
      weightUnit: this.weightUnit,
      minPrice: this.minPrice,
      blPrice: this.blPrice,
      originLocation: this.originLocation,
      destinationLocation: this.destinationLocation,
      contractNumber: this.contractNumber,
      restrictedClients: this.restrictedClients,
      restrictedUsers: this.restrictedUsers,
      rootParent: this.rootParent,
      chargeableWeight: this.chargeableWeight,
      chargeableWeightPrice: this.chargeableWeightPrice,
      maxWeightPerShipment: this.maxWeightPerShipment,
      maxWeightPerPiece: this.maxWeightPerPiece,
      maxMeasurementPerShipment: this.maxMeasurementPerShipment,
      maxMeasurementPerPiece: this.maxMeasurementPerPiece,
      maxTaxWeight: this.maxTaxWeight,
      maxLength: this.maxLength,
      maxHeight: this.maxHeight,
      maxWidth: this.maxWidth,
      maxLWH: this.maxLWH,
      minDensity: this.minDensity,
      maxDensity: this.maxDensity,
    });
  }
}
